/**
 * Base API Client
 * Provides a centralized HTTP client with automatic header injection
 * Follows the DRY principle and Factory pattern
 */

export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;

  constructor(status: number, statusText: string, body: Record<string, unknown>) {
    super(`API Error: ${status} ${statusText}`);
    this.status = status;
    this.body = body;
  }
}

const API_KEY = import.meta.env.VITE_API_KEY || "";

export interface RequestConfig extends RequestInit {
  params?: Record<string, string>;
}

/**
 * Base API client class that handles all HTTP requests
 * Automatically injects required headers and handles common configurations
 */
class ApiClient {
  private baseURL: string;
  private defaultHeaders: HeadersInit;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || "";
    this.defaultHeaders = {
      "Content-Type": "application/json",
      "x-api-key": API_KEY,
    };
  }

  /**
   * Merge custom headers with default headers
   */
  private getHeaders(customHeaders?: HeadersInit): HeadersInit {
    return {
      ...this.defaultHeaders,
      ...customHeaders,
    };
  }

  /**
   * Build full URL with optional query parameters
   */
  buildURL(endpoint: string, params?: Record<string, string>): string {
    // Use baseURL if set (for production/cross-origin), otherwise use current origin
    const base = this.baseURL || window.location.origin;
    const url = new URL(endpoint, base);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }
    
    // Return full URL when using baseURL (cross-origin), otherwise return path for same-origin
    return this.baseURL ? url.toString() : url.pathname + url.search;
  }

  /**
   * Generic request method
   */
  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { params, headers, ...restConfig } = config;
    
    const url = this.buildURL(endpoint, params);
    const mergedHeaders = this.getHeaders(headers);

    const response = await fetch(url, {
      ...restConfig,
      headers: mergedHeaders,
      credentials: 'include',
    });

    if (!response.ok) {
      let body: Record<string, unknown> = {};
      try {
        body = await response.json();
      } catch {
        // ignore parse errors
      }
      throw new ApiError(response.status, response.statusText, body);
    }

    // Handle empty responses (e.g., 204 No Content)
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }
    
    return {} as T;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "GET" });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "DELETE" });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
